import '@styles/index.css';
import React, { useState, useEffect } from 'react';
import {
    setEmail,
    setPassword,
    setDeviceUniqueID,
    setMobileNumber,
    setOtpType,
    setTenantUuid,
    setHospitalUuid,
    setHospitalInfoUuid,
    setUserUuid,
    setUserRole,
    setUserInfoUuid,
    setHospitalTenantUuid,
} from '@store/reducers/auth/signupSlice';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VERIFICATION_PATH, LOGIN_PATH, SIGNUP_PATH } from '@constants/routes';
import { EMAIL, HOSPITAL, NURSE, SMS, WAITING } from '@constants/constants';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import { OTP_SRC } from '@constants/images';
import { v4 as uuidv4 } from 'uuid';
import ErrorMessage from '@components/Error/ErrorMessage';
import { API_KEY } from '@constants/AppConfig';
import { HOSPITAL_DETAILS_API, SIGNUP_API } from '@constants/auth/Api';

const OTP = () => {
    const [deliveryMethod, setDeliveryMethod] = useState(EMAIL);
    const [userID, setUserID] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useAppDispatch();
    const email = useAppSelector((state) => state.signup.email);
    const mobileNumber = useAppSelector((state) => state.signup.mobileNumber);
    const password = useAppSelector((state) => state.signup.password);
    const deviceUniqueID = useAppSelector(
        (state) => state.signup.deviceUniqueID
    );
    const userRole = useAppSelector((state) => state.signup.userRole);
    const userState = useAppSelector((state) => state.signup.userState);
    const userName = useAppSelector((state) => state.signup.name);
    const hospitalName = useAppSelector((state) => state.signup.hospital_name);
    const hospitalBranchName = useAppSelector(
        (state) => state.signup.hospital_branch
    );
    const adminHospitalName = useAppSelector((state) => state.signup.name);
    const hospitalID = useAppSelector((state) => state.signup.hospital_id);
    const hospitalWebsite = useAppSelector(
        (state) => state.signup.hospital_website
    );
    const hospitalEmailID = useAppSelector((state) => state.signup.email_id);
    const hospitalContactNumber = useAppSelector(
        (state) => state.signup.contact_no
    );
    const hospitalGST = useAppSelector((state) => state.signup.gst_no);
    const logo_name = useAppSelector((state) => state.signup.logo_name);
    const logo_url = useAppSelector((state) => state.signup.logo_url);
    const logo_type = useAppSelector((state) => state.signup.logo_type);
    const userAddressOne = useAppSelector((state) => state.signup.address_one);
    const userAddressTwo = useAppSelector((state) => state.signup.address_two);
    const userCity = useAppSelector((state) => state.signup.city);
    const userCountry = useAppSelector((state) => state.signup.country);
    const userPincode = useAppSelector((state) => state.signup.pincode);
    const hospital_uuid = useAppSelector((state) => state.signup.hospital_id);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tenantUuid = uuidv4();
    const hospitalTenantUuid = uuidv4();
    const hospital_ID = uuidv4();
    const hospitalUuid = uuidv4();
    const hospitalInfoUuid = uuidv4();
    const userInfoUuid = uuidv4();
    const hospitalTelephone = useAppSelector(
        (state) => state.signup.telephoneNumber
    );
    const pushNotificationToken = useAppSelector((state) => state.user.pushNotificationToken);

    async function handleBeforeUnload(event: any) {
        const message = 'Are you sure you want to leave?';

        event.preventDefault();

        event.returnValue = message;

        return message;
    }

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);

        if (email == '') {
            navigate(LOGIN_PATH);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [email]);

    const obscureEmail = (email: string): string => {
        const [username, domain] = email.split('@');

        if (email != '') {
            const obscuredUsername = `${username.charAt(0)}${'*'.repeat(username.length - 2)}${username.charAt(username.length - 1)}`;
            const obscuredDomain = `${domain.charAt(0)}${'*'.repeat(domain.length - 2)}${domain.charAt(domain.length - 1)}`;

            return `${obscuredUsername}@${obscuredDomain}`;
        } else {
            return 's****@***m';
        }
    };

    let lastThreeDigits;

    if (mobileNumber != '') {
        lastThreeDigits = mobileNumber.slice(-3);
    } else {
        lastThreeDigits = '*******';
    }

    const handleDeliveryMethodChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setDeliveryMethod(event.target.value);
        dispatch(setOtpType(event.target.value));
    };

    function generateSlug(name: string) {
        const firstWord = name.split(' ')[0]; // Take only the first word
        const baseSlug = firstWord
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric characters with hyphens
            .replace(/(^-|-$)+/g, ''); // Remove leading and trailing hyphens
        const uniqueId = uuidv4().split('-')[0]; // Take a part of the UUID to ensure uniqueness
        return `${baseSlug}-${uniqueId}`;
    }

    const getHospitalDetailsFromAPI = async (hospitalId: string) => {
        try {
            const response = await fetch(
                HOSPITAL_DETAILS_API,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': API_KEY,
                    },
                    body: JSON.stringify({
                        hospital_id: hospitalId.toLowerCase(),
                    }),
                }
            );

            const result = await response.json();

            if (response.ok && result.data) {
                return result.data;
            } else {
                const errorMessage = result.message || 'Failed to retrieve hospital details';
                setError(errorMessage);
                return null;
            }
        } catch (error: any) {
            let errorMessage = 'Unexpected error occurred while fetching hospital details';

            if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
            return null;
        }
    };

    const handleSignup = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            let hospital_uuid_data = '';
            let hospital_name = '';
            let hospital_tenant_id = '';
            let nurse_hospital_id = '';
            let user_tenant_id = tenantUuid;
            // For Nurse role, validate hospital ID first
            if (userRole.toLowerCase() === NURSE.toLowerCase()) {
                const hospitalData = await getHospitalDetailsFromAPI(
                    hospitalID.toLowerCase()
                );
                if (!hospitalData) {
                    setError('Invalid hospital ID.');
                    setIsSubmitting(false);
                    return; // Stop execution if no hospital is found
                } else {
                    hospital_uuid_data = hospitalData?.hospital_id || '';
                    hospital_tenant_id = hospitalData?.tenant_id || '';
                    hospital_name = hospitalData?.hospital_name || '';
                    nurse_hospital_id = hospitalData?.hospital_id || '';
                    user_tenant_id = hospital_tenant_id;
                }
            }

            // Build request body based on user role
            const otpType = deliveryMethod === EMAIL ? 'email' : 'sms';
            let requestBody: any = {
                email,
                password,
                userRole,
                otpType,
                name: userName,
                mobileNumber,
                addressOne: userAddressOne,
                addressTwo: userAddressTwo,
                city: userCity,
                state: userState,
                country: userCountry,
                pincode: userPincode,
            };

            // Add role-specific fields
            if (userRole.toLowerCase() === NURSE.toLowerCase()) {
                requestBody.hospitalId = nurse_hospital_id.toLowerCase();
            } else if (userRole.toLowerCase() === HOSPITAL.toLowerCase()) {
                requestBody.gst = hospitalGST || '';
                requestBody.hospitalName = hospitalName;
                requestBody.hospitalEmailID = hospitalEmailID;
                requestBody.hospitalWebsite = hospitalWebsite;
                requestBody.hospitalContactNumber = hospitalContactNumber;
                requestBody.hospitalTelephone = hospitalTelephone;
            }

            // Call signup API
            const response = await fetch(SIGNUP_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                },
                body: JSON.stringify(requestBody),
            });

            const result = await response.json();

            if (!response.ok) {
                // Error response format: { message: 'Signup failed: ...' } or { message: 'Missing fields: ...' }
                const errorMessage = result.message || 'An unexpected error occurred during signup';
                setError(errorMessage);
                setIsSubmitting(false);
                return;
            }

            // Success response format: { message: 'Signup successful', userId: '...' }
            if (result.userId) {
                const userId = result.userId;
                setUserID(userId);
                
                dispatch(setUserRole(userRole));
                dispatch(setHospitalTenantUuid(hospitalTenantUuid));
                dispatch(setUserInfoUuid(userInfoUuid));
                dispatch(setTenantUuid(user_tenant_id));
                dispatch(setUserUuid(userId));
                dispatch(setHospitalUuid(hospitalUuid));
                dispatch(setHospitalInfoUuid(hospitalInfoUuid));
                dispatch(setDeviceUniqueID(deviceUniqueID));
                dispatch(setEmail(email));
                dispatch(setPassword(password));
                dispatch(setMobileNumber(mobileNumber));
                navigate(VERIFICATION_PATH);
            } else {
                setError('Signup successful but user ID not received');
                setIsSubmitting(false);
            }
        } catch (error: any) {
            let errorMessage = 'An unexpected error occurred during signup';

            if (error.message) {
                errorMessage = error.message;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            setError(errorMessage);
            setIsSubmitting(false);
        }
    };

    const handleBackClick = () => {
        navigate(SIGNUP_PATH);
    };

    return (
        <>
            <AuthLayout onBackClick={handleBackClick}>
                <div>
                    <div className="mx-auto flex justify-center my-5">
                        <img
                            src={OTP_SRC}
                            alt="OTP"
                            className="w-32 h-32 object-contain"
                        />
                    </div>
                    <div className="text-center">
                        <h4 className="text-2xl font-bold text-primary mb-2">
                            {t('auth.otp_verification')}
                        </h4>
                        <p className="text-md text-center text-secondary mb-0">
                            {t('auth.otp_text')}
                        </p>

                        {error && <ErrorMessage message={error} />}
                    </div>
                    <div>
                        <div className="flex flex-col items-center justify-center">
                            <div className="card 2xl:w-2/6 xl:w-[35%]  shadow-none mb-3">
                                <div className="card-body 2xl:px-5 xl:px-5 lg:px-8 py-2">
                                    <div className="2xl:mt-2 2xl:ml-14 lg:mt-0 lg:ml-0 xl:ml-9 xl:mt-0">
                                        <div className="radioButtonContainer radio-button-margin mt-2">
                                            <input
                                                type="radio"
                                                id="email"
                                                value={EMAIL}
                                                checked={
                                                    deliveryMethod === EMAIL
                                                }
                                                onChange={
                                                    handleDeliveryMethodChange
                                                }
                                                className="radioButton mr-2"
                                            />
                                            <label
                                                htmlFor="email"
                                                className="cursor-pointer ml-2 text-nero font-normal"
                                            >
                                                {t('auth.otp_email')} <br />
                                                <span className="text-secondary">
                                                    {obscureEmail(email)}
                                                </span>
                                            </label>
                                        </div>
                                        <div className="radioButtonContainer radio-button-margin my-2">
                                            {/* <input
                                                type="radio"
                                                id="text"
                                                value={SMS}
                                                checked={deliveryMethod === SMS}
                                                onChange={
                                                    handleDeliveryMethodChange
                                                }
                                                className="radioButton mr-2"
                                            />
                                            <label
                                                htmlFor="text"
                                                className="cursor-pointer ml-2 text-nero font-normal"
                                            >
                                                {t('auth.otp_mobile')} <br />
                                                <span className="text-secondary">
                                                    xxxxxxx{lastThreeDigits}
                                                </span>
                                            </label> */}
                                        </div>
                                    </div>
                                    <div className="mx-auto flex items-center justify-center">
                                        <button
                                            type="submit"
                                            onClick={handleSignup}
                                            disabled={isSubmitting}
                                            className="btn w-5/6 mt-4 py-3 px-4 !bg-primary !hover:bg-primary text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {t('auth.submit')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AuthLayout>
        </>
    );
};

export default OTP;
